import {
  EScheduledExportFrequency,
  IScheduledExport,
  LAST_DAY_OF_MONTH,
  WEEKDAY_KEYS,
  scheduledExportModule,
} from "@rentiohq/shared-frontend/dist/reduxV2/scheduledExport";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  DisplayText,
  EmptyState,
  Modal,
  ResourceList,
  ResourceListItem,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import { useDispatch } from "react-redux";
import { renderJoined } from "./renderJoined";

interface IProps {
  scheduledExports: IScheduledExport[];
  onChange: () => void;
  onCreate: () => void;
  canCreate: boolean;
  onClose: () => void;
}

// "Every week on Monday", "Every month on day 15", "Every month on the last day", "Every year on
// 31 December", "Every year on the last day of February".
const getScheduleLabel = (scheduledExport: IScheduledExport) => {
  const { frequency, runDay, runMonth } = scheduledExport;
  const frequencyLabel = getLocalizedText(
    `scheduled_export.frequency.${frequency}`.toLowerCase(),
  );
  if (runDay == null) {
    return frequencyLabel;
  }

  if (frequency === EScheduledExportFrequency.Weekly) {
    const weekdayKey = WEEKDAY_KEYS[runDay - 1];
    if (!weekdayKey) {
      return frequencyLabel;
    }

    return getLocalizedText("scheduled_export.schedule.on", {
      frequency: frequencyLabel,
      day: getLocalizedText(weekdayKey),
    });
  }

  const isLastDay = runDay === LAST_DAY_OF_MONTH;
  if (frequency === EScheduledExportFrequency.Yearly && runMonth) {
    const month = getLocalizedText(`system.month.${runMonth}`);

    return isLastDay
      ? getLocalizedText("scheduled_export.schedule.on_last_day_of_month", {
          frequency: frequencyLabel,
          month,
        })
      : getLocalizedText("scheduled_export.schedule.on", {
          frequency: frequencyLabel,
          day: `${runDay} ${month}`,
        });
  }

  return isLastDay
    ? getLocalizedText("scheduled_export.schedule.on_last_day", {
        frequency: frequencyLabel,
      })
    : getLocalizedText("scheduled_export.schedule.on_day", {
        frequency: frequencyLabel,
        day: `${runDay}`,
      });
};

export const ScheduledExportsModal = (props: IProps) => {
  const { scheduledExports, onChange, onCreate, canCreate, onClose } = props;

  const dispatch = useDispatch();

  // One request per schedule at a time: a second click while the first is on its way is ignored.
  const busyIds = React.useRef(new Set<string>());
  const runOnce = (id: string, fn: () => Promise<void>) => async () => {
    if (busyIds.current.has(id)) {
      return;
    }
    busyIds.current.add(id);
    try {
      await fn();
    } finally {
      busyIds.current.delete(id);
    }
  };

  // Event handlers
  const generateHandleToggleActive = (scheduledExport: IScheduledExport) =>
    runOnce(scheduledExport.id, async () => {
      try {
        await scheduledExportModule.promises.update(dispatch, {
          id: scheduledExport.id,
          data: { active: !scheduledExport.active },
        });
      } catch (error) {
        showAlert({ type: "error", error });
      } finally {
        onChange();
      }
    });

  const generateHandleRemove = (scheduledExport: IScheduledExport) => () => {
    confirm({
      type: "warning",
      modalProps: { hasDismiss: true, shouldCloseOnOverlayClick: true },
      title: getLocalizedText("scheduled_export.delete.title"),
      info: getLocalizedText("scheduled_export.delete.info"),
      primaryActions: [{ title: getLocalizedText("system.no") }],
      secondaryAction: {
        title: getLocalizedText("system.yes"),
        onPress: runOnce(scheduledExport.id, async () => {
          try {
            await scheduledExportModule.promises.remove(dispatch, {
              id: scheduledExport.id,
            });
          } catch (error) {
            showAlert({ type: "error", error });
          } finally {
            onChange();
          }
        }),
      },
    });
  };

  // Render
  const renderItem = (item: IScheduledExport) => (
    <ResourceListItem
      actions={[
        {
          content: getLocalizedText(
            item.active
              ? "scheduled_export.action.pause"
              : "scheduled_export.action.resume",
          ),
          onClick: generateHandleToggleActive(item),
        },
        {
          content: getLocalizedText("scheduled_export.action.delete"),
          onClick: generateHandleRemove(item),
        },
      ]}
    >
      <DisplayText size="small" space="extraTight">
        {getLocalizedText(
          `export_file.export_type.${item.exportType}`.toLowerCase(),
        )}
      </DisplayText>

      <TextStyle variation="subdued">
        {renderJoined([
          getScheduleLabel(item),
          item.active ? (
            getLocalizedText("scheduled_export.next_run", {
              date: formatDate(item.nextRunAt),
            })
          ) : (
            <TextStyle variation="warn">
              {getLocalizedText("scheduled_export.paused")}
            </TextStyle>
          ),
          item.exportFileType,
        ])}
      </TextStyle>
    </ResourceListItem>
  );

  return (
    <Modal
      heading={getLocalizedText("scheduled_export.list.title")}
      onClose={onClose}
      shouldCloseOnOverlayClick={true}
      hasDismiss={true}
      width="medium"
      actions={[
        {
          content: getLocalizedText("export_file.create.cta"),
          appearance: "primary",
          isDisabled: !canCreate,
          onClick: onCreate,
        },
      ]}
    >
      {scheduledExports.length === 0 ? (
        <EmptyState
          icon="archive"
          heading={getLocalizedText("scheduled_export.list.empty")}
        />
      ) : (
        <ResourceList items={scheduledExports} renderItem={renderItem} />
      )}
    </Modal>
  );
};
